const flex = {
    name: "flex",
    path: "/flex",
    title: "flex",
    description: "Utilities for controlling how flex items both grow and shrink.",
    value: {
        'flex': {
            "flex-<number>": "<number>",
            "flex-auto": "1 1 auto",
            "flex-initial": "0 1 auto",
            "flex-none": "none",
            "flex-[<value>]": "<value>",
        },
    },
    exampleData: [
        {
            "example": {
                "code": `
<div class="flex">
  <div class="w-14 flex-none ...">01</div>
  <div class="w-64 flex-1 ...">02</div>
  <div class="w-32 flex-1 ...">03</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="flex">
  <div class="w-14 flex-none ...">01</div>
  <div class="w-64 flex-initial ...">02</div>
  <div class="w-32 flex-initial ...">03</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="flex ...">
  <div class="w-14 flex-none ...">01</div>
  <div class="w-64 flex-auto ...">02</div>
  <div class="w-32 flex-auto ...">03</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="flex ...">
  <div class="w-14 flex-none ...">01</div>
  <div class="w-32 flex-none ...">02</div>
  <div class="flex-1 ...">03</div>
</div>
                `
            },
        },
        {
            "example": {
                "code": `
<div class="flex-[3_1_auto] ...">
  <!-- ... -->
</div>
                `
            },
        },
    ],
    htmlData: [
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">flex-<var>&lt;number&gt;</var></code>
                 utilities like flex-1 to allow a flex item to grow and shrink as needed, ignoring its initial size:
            </p>

            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="flex gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex h-14 w-14 flex-none items-center justify-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-color-pink-400">01</div>
                    <div class="flex w-64 flex-1 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex w-32 flex-1 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>
                </div>
            </div>

            
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">flex-initial</code>
                   to allow a flex item to shrink but not grow, taking into account its initial size:
            </p>

            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="flex gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex h-14 w-14 flex-none items-center justify-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-color-pink-400">01</div>
                    <div class="flex w-64 flex-initial items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex w-32 flex-initial items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">flex-auto</code>
                  to allow a flex item to grow and shrink, taking into account its initial size:
            </p>

             <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="flex gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex h-14 w-14 flex-none items-center justify-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-color-pink-400">01</div>
                    <div class="flex w-64 flex-auto items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex w-32 flex-auto items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>
                </div>
            </div>
        
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">flex-none</code>
                 to prevent a flex item from growing or shrinking:
            </p>     
            
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="flex gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex h-14 w-14 flex-none items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex w-64 flex-none items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex w-32 flex-1 items-center justify-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-color-pink-400">03</div>
                </div>
            </div>
            `
        },
        {
            data: `
            <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
                Use the <code class="text-color-gray-800 dark:text-color-white font-semibold">flex-[<value>]</code>
                 syntax to set the flex shorthand property based on a completely custom value:
            </p>     
            
            <div class="relative">
                <div class="absolute inset-0 z-0">
                    <div class="h-full rounded-lg border bg-size-[8px_8px] text-color-gray-200 dark:text-color-gray-700 bg-top-left bg-image-[repeating-linear-gradient(315deg,currentColor_0_1px,transparent_1%,transparent_50%)] border border-color-gray-200 rounded-lg dark:border-color-gray-700"></div>
                </div>
                <div class="flex gap-4 font-mono text-sm leading-6 font-bold text-color-white relative">
                    <div class="flex h-14 w-14 flex-[3_1_auto] items-center justify-center rounded-lg bg-color-pink-500 p-4">01</div>
                    <div class="flex w-64 flex-none items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex w-32 flex-1 items-center justify-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-color-pink-400">03</div>
                </div>
            </div>
            `
        },
    ]
};

export default flex;

<div className="sr-only">

<div class="flex z-0 gap-4 flex-initial flex-[3_1_auto] flex-auto font-mono text-sm leading-6 font-bold text-color-white">
                    <div class="flex h-14 w-14 flex-none items-center justify-center rounded-lg bg-color-pink-300 p-4 dark:bg-color-pink-800 dark:text-color-pink-400">01</div>
                    <div class="flex w-64 flex-1 items-center justify-center rounded-lg bg-color-pink-500 p-4">02</div>
                    <div class="flex w-32 flex-1 items-center justify-center rounded-lg bg-color-pink-500 p-4">03</div>
                </div>

</div>